import { useState } from 'react';
import { Globe, Link2, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useBrand } from '@/config/brands';
import { toast } from 'sonner';

interface ScenarioVisibilityToggleProps {
  visibility: 'public' | 'unlisted';
  onVisibilityChange: (visibility: 'public' | 'unlisted') => void;
  shareUrl?: string;
}

export function ScenarioVisibilityToggle({
  visibility,
  onVisibilityChange,
  shareUrl,
}: ScenarioVisibilityToggleProps) {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Share link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium">Visibility</label>

      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => onVisibilityChange('public')}
          className={`p-3 rounded-lg border-2 text-left transition-all ${
            visibility === 'public'
              ? isAhp
                ? 'border-blue-500 bg-blue-50'
                : 'border-matrix-green bg-matrix-green/10'
              : isAhp
                ? 'border-gray-200 hover:border-blue-300'
                : 'border-matrix-green/20 hover:border-matrix-green/40'
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <Globe className={`w-4 h-4 ${
              visibility === 'public'
                ? isAhp ? 'text-blue-600' : 'text-matrix-green'
                : 'text-muted-foreground'
            }`} />
            <span className="text-sm font-semibold">Public</span>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Searchable by community. Help others facing the same situation.
          </p>
        </button>

        <button
          type="button"
          onClick={() => onVisibilityChange('unlisted')}
          className={`p-3 rounded-lg border-2 text-left transition-all ${
            visibility === 'unlisted'
              ? isAhp
                ? 'border-blue-500 bg-blue-50'
                : 'border-matrix-green bg-matrix-green/10'
              : isAhp
                ? 'border-gray-200 hover:border-blue-300'
                : 'border-matrix-green/20 hover:border-matrix-green/40'
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <Link2 className={`w-4 h-4 ${
              visibility === 'unlisted'
                ? isAhp ? 'text-blue-600' : 'text-matrix-green'
                : 'text-muted-foreground'
            }`} />
            <span className="text-sm font-semibold">Unlisted</span>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Only accessible via direct link. Share with family, doctor, or insurer.
          </p>
        </button>
      </div>

      {shareUrl && (
        <div className="flex items-center gap-2 mt-2">
          <Input
            value={shareUrl}
            readOnly
            className="text-xs font-mono bg-black/10 flex-1"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="shrink-0"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
      )}
    </div>
  );
}
